import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { PanAppState } from '@panamax/app-state';
import { OrderRow } from '@shared/models/my-orders-view.model';
import { DisplayType } from '@shared/constants/order-status-title.enum';
import { FEATURES } from '@shared/constants/splitio-features';
import { OrdersListComponent } from '../orders-list-base.component';
import { Router } from '@angular/router';
import { PlatformService } from '@panamax/app-state';
import { OrderExceptionService } from '@order/pages/order-exceptions/service/order-exception.service';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { PopoverController } from '@ionic/angular';
import { MessageHandlerService } from '@shared/services/message/message-handler.service';
import { MyOrdersTracingService } from 'src/app/my-orders/tracking/tracing/my-orders-tracing.service';
import { OrderTracingService } from '../../../tracking/tracing/order-tracing.service';
import { OrderSubmittedAnalyticsService } from 'src/app/order/pages/order-submitted/tracking/analytics/order-submitted-analytics.service';
import { ModalService } from '@shared/services/modal/modal.service';
import { ModalController } from '@ionic/angular';
import { OrderService } from '../../../services/order.service';
import { MyOrdersStoreService, OrderStatus } from '@usf/ngrx-order';
import {
  OrderActionsBar,
  OrderLinks,
} from '@shared/models/order-actions-bar.model';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-order-card-actions-bar',
  templateUrl: './order-card-actions-bar.component.html',
  styleUrls: ['./order-card-actions-bar.component.scss'],
})
export class OrderCardActionsBarComponent
  extends OrdersListComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() item: OrderRow;
  @Input() isInternalUser: boolean;
  @Input() isOnline: boolean;
  actionLinks: OrderActionsBar;
  insightsCount: number;
  displayBar: boolean;
  readonly DisplayType = DisplayType;
  readonly insightsOrderLinkFeatureFlag =
    FEATURES.split_global_order_insight.name;
  readonly dynamicCookbookOrderFeatureFlag =
    FEATURES.split_dynamic_order_cookbook.name;
  readonly newOrderCardLinkFlag = FEATURES.split_dynamic_order_cookbook.name;
  private destroy$ = new Subject<void>();

  constructor(
    myOrderStoreService: MyOrdersStoreService,
    orderService: OrderService,
    router: Router,
    platformService: PlatformService,
    orderExceptionService: OrderExceptionService,
    navigationHelperService: NavigationHelperService,
    popoverController: PopoverController,
    messageHandlerService: MessageHandlerService,
    panAppState: PanAppState,
    myOrdersTracingService: MyOrdersTracingService,
    orderTracingService: OrderTracingService,
    orderSubmittedAnalyticsService: OrderSubmittedAnalyticsService,
    modalService: ModalService,
    modalController: ModalController,
  ) {
    super(
      myOrderStoreService,
      orderService,
      router,
      platformService,
      orderExceptionService,
      navigationHelperService,
      popoverController,
      messageHandlerService,
      panAppState,
      myOrdersTracingService,
      orderTracingService,
      orderSubmittedAnalyticsService,
      modalService,
      modalController,
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.displayBar =
      this.platformService.platformType !==
      this.platformService.platformEnum.desktop;
    if (this.item) {
      this.initializeActionLinks(this.isOnline);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes['item'] && changes['item'].currentValue) ||
      (changes['isOnline'] &&
        changes['isOnline'].currentValue !== changes['isOnline'].previousValue)
    ) {
      this.initializeActionLinks(this.isOnline);
    }
  }

  insightsItems(item: OrderRow) {
    return (item?.orderHeader?.dyfProductsNotOrdered || []).concat(
      item?.orderHeader?.newProductsOrderedNotInRecentlyPurchased || [],
    );
  }

  shouldDisplayResolveExceptionsLink(orderRow: OrderRow): boolean {
    return (
      orderRow?.isEditable &&
      orderRow?.displayType === DisplayType.SUBMITTED_WITH_EXCEPTIONS &&
      !orderRow?.restrictToOG &&
      orderRow?.orderHeader?.orderStatus !== OrderStatus.SHIPPED_WITH_EXCEPTIONS
    );
  }

  displayModifyLink(item: OrderRow): boolean {
    return (
      this.item.isEditable &&
      !(
        this.item.displayType === DisplayType.SUBMITTED_WITH_EXCEPTIONS &&
        !this.item.restrictToOG &&
        this.item?.orderHeader?.orderType === 'RT' &&
        this.insightsCount > 0 &&
        this.isOnline &&
        this.isInternalUser
      )
    );
  }

  displayInsightsLink(item: OrderRow): boolean {
    if (!this.isInternalUser || !this.isOnline) {
      return false;
    }

    if (item?.orderHeader?.orderType === 'RT') {
      if (this.insightsCount === 0) {
        return item?.orderHeader?.cookbookAdoptionPercent > 0;
      } else if (this.insightsCount > 0) {
        return true;
      }
    }
    return false;
  }

  displayTrackDeliveryLink(item: OrderRow): boolean {
    return (
      (this.item.hasETADetails ||
        (!this.item.orderHeader?.orderDelivered &&
          !!this.item.orderHeader.etaRoute) ||
        (this.item.orderHeader.trackingAvailable &&
          !!this.item.orderHeader.orderTracking?.length) ||
        !!this.item.wmtDeliveryDate ||
        !!this.item.orderHeader.orderDelivered ||
        (this.item.orderHeader?.orderType === 'VS' &&
          (!!this.item.orderHeader?.orderTracking?.length ||
            this.item.orderHeader?.trackingAvailable))) &&
      !(this.hideEtaGlobalFeatureFlag && this.item?.orderHeader?.hideEta)
    );
  }

  private initializeActionLinks(online: boolean) {
    // Safety check: ensure item is available
    if (!this.item) {
      return;
    }
    this.insightsCount = this.insightsItems(this.item)?.length;
    let links: OrderLinks[] = [
      // // Track Delivery Link
      {
        title: 'i18n.orderStatusPage.trackDelivery',
        dataCy: 'open-order-tracking-link',
        displayLink: this.displayTrackDeliveryLink(this.item),
        linkAction: () => this.openOrder(this.item, true),
      },
      // Modify Link
      {
        title: this.item.modifyLinkText || 'i18n.common.modify',
        dataCy: 'edit-order-link',
        displayLink: this.displayModifyLink(this.item),
        linkAction: event => this.editSubmittedOrder(this.item, event),
      },
      // // Resubmit Link (Order Error)
      {
        title: 'i18n.common.resubmit',
        dataCy: 'resubmit-order-link',
        displayLink: this.item.displayType === DisplayType.ORDER_ERROR,
        linkAction: event => this.openOrder(this.item, true, event),
      },
      // // Create Order Link
      {
        title: 'i18n.common.createOrder',
        dataCy: 'create-order-link',
        displayLink: this.item.displayType === DisplayType.NO_ORDER,
        linkAction: () => this.createNewOrder(this.item),
      },
      // Resolve Exceptions Link
      {
        title: 'i18n.orderStatusPage.resolve',
        dataCy: 'resolve-exception-button',
        displayLink: this.shouldDisplayResolveExceptionsLink(this.item),
        linkAction: event => this.resolveSubmittedOrder(this.item, event),
      },
      // // Resubmit Submission Error Link
      {
        title: 'i18n.orderStatusPage.resubmit',
        dataCy: 'resubmit-submission-error-link',
        displayLink: this.item.displayType === DisplayType.SUBMISSION_ERROR,
        linkAction: event => this.resubmitOrder(this.item, event),
      },
      // // Insights Link
      {
        title: 'i18n.orderStatusPage.insights',
        displayFlag: this.insightsOrderLinkFeatureFlag,
        dataCy: 'insights-order-link',
        displayLink: this.displayInsightsLink(this.item),
        linkAction: event => this.openInsightsOrder(this.item, event),
        badge: this.insightsCount,
      },
    ];

    // Filter out links that shouldn't be displayed
    links = links.filter(link => {
      if (
        this.item.orderHeader?.orderType === 'VS' &&
        this.item.displayType === DisplayType.ORDER_CANCELLED
      ) {
        return false;
      }
      return link.displayLink;
    });

    this.actionLinks = {
      orderLinks: links,
      lineDivider: true,
    };
  }
}
